/**
 * Household Verification Summary (Combined Report.png) — INTERACTIVE. The
 * tiles (ID / Income / Rent / Court Records / Credit) each open a popover
 * listing the applicants who have that verification (per-member availability
 * from the loader's `memberVerifications` / `memberCourtRecords`). ID/Income/
 * Rent "View" navigates to that member's individual page (per-member report
 * data lives there, not on the combined DTO — keeps this page light); Court
 * Records "View" opens that member's court-record cards; Credit "Preview"
 * opens the inline PDF dialog. The tile FACE keeps the family-payload status
 * for id/income/rent (data-map §2); the court face is the household rollup
 * (rollupHouseholdCourtRecords); the credit face reflects how many members
 * have a PDF. A tile nobody has reads "Not Available", exactly like the
 * individual page (client 2026-09-08). The Court Records tile gates on
 * OpenRoom being enabled, as on the individual page.
 */
import {
	Banknote,
	ChevronRight,
	CircleDollarSign,
	ClipboardPlus,
	Eye,
	FileText,
	ShieldCheck,
} from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '#app/components/ui/popover'
import {
	type FamilyPredictions,
	rollupHouseholdCourtRecords,
} from '#app/utils/application-detail-v2/combined-display'
import {
	courtRecordRefs,
	courtRecordsTileView,
	verificationBarTone,
} from '#app/utils/application-detail-v2/display'
import { preserveReturnTo } from '#app/utils/application-detail-v2/return-to'
import type {
	GroupMember,
	MemberVerificationAvailability,
	OpenroomSummary,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import {
	type BarTone,
	MutedText,
	VerificationTile,
} from '../VerificationSummary'
import { CourtRecordsDialog } from '../dialogs/CourtRecordsDialog'
import { CreditPreviewDialog } from '../dialogs/CreditPreviewDialog'
import { DETAIL_CARD, SectionIcon } from '../primitives'

type VerificationKey = keyof MemberVerificationAvailability

/** One applicant row inside a tile's popover. */
function MemberRow({
	member,
	action,
}: {
	member: GroupMember
	action: React.ReactNode
}) {
	return (
		<li className="flex items-center justify-between gap-3 rounded-lg px-2 py-2">
			<div className="flex min-w-0 items-center gap-2.5">
				<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#32302F] text-xs font-medium text-white">
					{member.displayName.charAt(0).toUpperCase()}
				</span>
				<div className="min-w-0">
					<p className="truncate text-sm font-medium text-[#32302F]">
						{member.displayName}
					</p>
					<p className="truncate text-[11px] text-[#747474]">
						{member.relationshipLabel}
					</p>
				</div>
			</div>
			{action}
		</li>
	)
}

function TilePopover({
	tile,
	available,
	emptyHint,
	children,
}: {
	tile: React.ReactNode
	available: number
	emptyHint: string
	children: React.ReactNode
}) {
	return (
		<Popover>
			<PopoverTrigger className="w-full cursor-pointer text-left outline-none">
				{tile}
			</PopoverTrigger>
			<PopoverContent align="center" sideOffset={8} className="w-[300px] p-2">
				{available === 0 ? (
					<p className="px-2 py-3 text-center text-sm text-[#747474]">
						{emptyHint}
					</p>
				) : (
					<ul className="flex flex-col gap-1">{children}</ul>
				)}
			</PopoverContent>
		</Popover>
	)
}

const PILL_BUTTON =
	'flex shrink-0 cursor-pointer items-center gap-1 rounded-full bg-[#32302F] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#1a1918]'

export function CombinedVerificationSummary({
	familyPredictions,
	members,
	memberVerifications,
	memberCourtRecords,
	openroomEnabled,
	propertyId,
	canDownload,
}: {
	familyPredictions: FamilyPredictions | null
	members: GroupMember[]
	memberVerifications: Record<string, MemberVerificationAvailability>
	/** Per-member court-records summary (FULL members only; null = no data). */
	memberCourtRecords: Record<string, OpenroomSummary | null>
	/** OPENROOM_ENABLED — the Court Records tile only shows when OpenRoom is
	 * enabled platform-wide, exactly like the individual summary. */
	openroomEnabled: boolean
	propertyId: string
	/** `can.download` (data.export). The credit "Preview" serves the full
	 * TenCheck PDF via the export-gated blob route, so it hides without it;
	 * the id/income/rent "View" actions only navigate and stay. */
	canDownload: boolean
}) {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const [creditPreview, setCreditPreview] = useState<GroupMember | null>(null)
	const [courtPreview, setCourtPreview] = useState<GroupMember | null>(null)

	if (!familyPredictions) return null
	const { verificationSummary } = familyPredictions

	const availableFor = (key: VerificationKey) =>
		members.filter((m) => memberVerifications[m.applicationId]?.[key])

	const idMembers = availableFor('id')
	const incomeMembers = availableFor('income')
	const rentMembers = availableFor('rent')
	const creditMembers = availableFor('credit')
	// Members with ANY court data (a paused/pending/failed/clean run counts —
	// the row states it); "View" only where records were found.
	const courtMembers = members.filter(
		(m) => memberCourtRecords[m.applicationId] != null,
	)
	const householdCourtStatus = rollupHouseholdCourtRecords(
		members.map((m) => memberCourtRecords[m.applicationId]),
	)
	const householdCourt = courtRecordsTileView(
		householdCourtStatus ? { status: householdCourtStatus, url: null } : null,
	)

	const goToMember = (applicationId: string) =>
		void navigate(
			preserveReturnTo(
				`/properties/${propertyId}/applications/${applicationId}/v2`,
				searchParams,
			),
		)

	const viewAction = (member: GroupMember) => (
		<button
			type="button"
			onClick={() => goToMember(member.applicationId)}
			className={PILL_BUTTON}
		>
			View
			<ChevronRight className="h-3.5 w-3.5" />
		</button>
	)

	const previewAction = (member: GroupMember) => (
		<button
			type="button"
			onClick={() => setCreditPreview(member)}
			className={PILL_BUTTON}
		>
			<Eye className="h-3.5 w-3.5" />
			Preview
		</button>
	)

	const courtAction = (member: GroupMember) => {
		const view = courtRecordsTileView(memberCourtRecords[member.applicationId])
		const refs = courtRecordRefs(memberCourtRecords[member.applicationId])
		return view.url || refs.length > 0 ? (
			<button
				type="button"
				onClick={() => setCourtPreview(member)}
				className={PILL_BUTTON}
			>
				<Eye className="h-3.5 w-3.5" />
				View
			</button>
		) : (
			<span className="shrink-0 text-xs text-[#747474]">{view.text}</span>
		)
	}

	// "N applicants" / "N reports" on the face; nobody → "Not Available",
	// matching the individual tiles (client 2026-09-08).
	const countLabel = (n: number, noun = 'applicant') =>
		n === 0 ? 'Not Available' : `${n} ${noun}${n === 1 ? '' : 's'}`

	const creditTone: BarTone = creditMembers.length === 0 ? 'neutral' : 'green'

	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex items-center gap-3">
				<SectionIcon src="/img/verification-summary-icon.svg" />
				<h2 className="text-[19px] font-semibold text-[#32302F]">
					Verification Summary
				</h2>
			</div>
			<div
				className={cn(
					'mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2',
					openroomEnabled ? 'xl:grid-cols-5' : 'xl:grid-cols-4',
				)}
			>
				<TilePopover
					tile={
						<VerificationTile
							tone="green"
							icon={<ShieldCheck />}
							title="ID Verification"
							barTone={verificationBarTone(verificationSummary.id?.status)}
						>
							<MutedText>{countLabel(idMembers.length)}</MutedText>
						</VerificationTile>
					}
					available={idMembers.length}
					emptyHint="No applicant has ID verification data."
				>
					{idMembers.map((m) => (
						<MemberRow
							key={m.applicationId}
							member={m}
							action={viewAction(m)}
						/>
					))}
				</TilePopover>

				<TilePopover
					tile={
						<VerificationTile
							tone="rose"
							icon={<Banknote />}
							title="Income Verification"
							barTone={verificationBarTone(verificationSummary.income?.status)}
						>
							<MutedText>{countLabel(incomeMembers.length)}</MutedText>
						</VerificationTile>
					}
					available={incomeMembers.length}
					emptyHint="No applicant has income verification data."
				>
					{incomeMembers.map((m) => (
						<MemberRow
							key={m.applicationId}
							member={m}
							action={viewAction(m)}
						/>
					))}
				</TilePopover>

				<TilePopover
					tile={
						<VerificationTile
							tone="green"
							icon={<CircleDollarSign />}
							title="Rent Verification"
							barTone={verificationBarTone(verificationSummary.rent?.status)}
						>
							<MutedText>{countLabel(rentMembers.length)}</MutedText>
						</VerificationTile>
					}
					available={rentMembers.length}
					emptyHint="No applicant has rent verification data."
				>
					{rentMembers.map((m) => (
						<MemberRow
							key={m.applicationId}
							member={m}
							action={viewAction(m)}
						/>
					))}
				</TilePopover>

				{openroomEnabled ? (
					<TilePopover
						tile={
							<VerificationTile
								tone="purple"
								icon={<FileText />}
								title="Court Records"
								barTone={householdCourt.barTone}
							>
								<MutedText>{householdCourt.text}</MutedText>
							</VerificationTile>
						}
						available={courtMembers.length}
						emptyHint="No applicant has court records data."
					>
						{courtMembers.map((m) => (
							<MemberRow
								key={m.applicationId}
								member={m}
								action={courtAction(m)}
							/>
						))}
					</TilePopover>
				) : null}

				<TilePopover
					tile={
						<VerificationTile
							tone="rose"
							icon={<ClipboardPlus />}
							title="Credit Report"
							barTone={creditTone}
						>
							<MutedText>
								{countLabel(creditMembers.length, 'report')}
							</MutedText>
						</VerificationTile>
					}
					available={creditMembers.length}
					emptyHint="No applicant has a credit report."
				>
					{creditMembers.map((m) => (
						<MemberRow
							key={m.applicationId}
							member={m}
							action={canDownload ? previewAction(m) : null}
						/>
					))}
				</TilePopover>
			</div>

			<CreditPreviewDialog
				open={creditPreview !== null}
				onOpenChange={(next) => {
					if (!next) setCreditPreview(null)
				}}
				applicationId={creditPreview?.applicationId ?? null}
				propertyId={propertyId}
				applicantName={creditPreview?.displayName ?? null}
			/>
			<CourtRecordsDialog
				open={courtPreview !== null}
				onOpenChange={(next) => {
					if (!next) setCourtPreview(null)
				}}
				records={courtRecordRefs(
					courtPreview ? memberCourtRecords[courtPreview.applicationId] : null,
				)}
			/>
		</section>
	)
}
